/**
 * MobileNotifBell.jsx — Standalone notification bell for mobile
 *
 * Calls fetchNotifications directly from feedQueries.js
 * Does NOT require FeedProvider wrapping.
 * Uses Firebase UID as feed user ID (same as useFeedUser.js).
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { auth } from "../../firebase";
import { fetchNotifications } from "../../lib/feedQueries";
import { formatTimeAgo } from "../../lib/utils";

const NOTIF_EMOJI = {
  upvote: "👍", heart: "❤️", haha: "😂", think: "🤔", wow: "😮",
  comment: "💬", follow: "👤", mention: "📣", reply: "↩️", system: "🔔",
};

function buildMessage(notif) {
  const actor = notif.actor_username ? `@${notif.actor_username}` : "Someone";
  switch (notif.type) {
    case "upvote":  return `${actor} reacted to your post`;
    case "heart":   return `${actor} loved your post`;
    case "comment": return `${actor} commented on your post`;
    case "follow":  return `${actor} started following you`;
    case "mention": return `${actor} mentioned you`;
    case "reply":   return `${actor} replied to your comment`;
    default:        return `${actor} interacted with your post`;
  }
}

export default function MobileNotifBell() {
  const [notifs, setNotifs]       = useState([]);
  const [unread, setUnread]       = useState(0);
  const [open, setOpen]           = useState(false);
  const [loading, setLoading]     = useState(false);
  const dropdownRef               = useRef(null);
  const fetchedRef                = useRef(false);

  const doFetch = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    setLoading(true);
    try {
      const data = await fetchNotifications(user.uid);
      setNotifs(data || []);
      setUnread((data || []).filter(n => !n.is_read).length);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  // Fetch once on mount
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    doFetch();
  }, [doFetch]);

  // Refetch on tab return (throttled 5min)
  const lastFetchRef = useRef(0);
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastFetchRef.current < 300000) return;
      lastFetchRef.current = Date.now();
      doFetch();
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [doFetch]);

  // Outside click closes
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={dropdownRef} className="hh-notif-wrap">
      <button className="hh-topbar-btn" onClick={() => { setOpen(o => !o); if (!open) doFetch(); }}
        aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ""}`}>
        🔔
        {unread > 0 && <span className="hh-notif-badge">{unread > 99 ? "99+" : unread}</span>}
      </button>

      {open && (
        <div className="hh-notif-dropdown">
          <div className="hh-notif-header">
            <span className="hh-notif-title">Notifications</span>
            <span className="hh-notif-status">
              {loading ? "Refreshing…" : unread > 0 ? `${unread} new` : "Up to date"}
            </span>
          </div>
          <div className="hh-notif-list">
            {notifs.length === 0 ? (
              <div className="hh-notif-empty">{loading ? "Loading…" : "No notifications yet"}</div>
            ) : (
              notifs.slice(0, 20).map(n => (
                <div key={n.id} className={`hh-notif-row${n.is_read ? "" : " unread"}`}>
                  <span className="hh-notif-emoji">{NOTIF_EMOJI[n.type] || "🔔"}</span>
                  <div className="hh-notif-body">
                    <div className="hh-notif-msg">{n.message || buildMessage(n)}</div>
                    <div className="hh-notif-time">{formatTimeAgo(n.created_at)}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}